import type React from "react";
import {
  Box,
  Button,
  MenuItem,
  Radio,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CustomTextField from "../../../../components/ui/Textfield";
import CustomSelect from "../../../../components/ui/Select";
import IconButton from "../../../../components/ui/IconButton";
import type { CompetencyQuestion } from "../../types";

interface CompetencyQuestionEditorProps {
  questions: CompetencyQuestion[];
  onChange: (questions: CompetencyQuestion[]) => void;
}

const QUESTION_TYPES: { value: CompetencyQuestion["type"]; label: string }[] = [
  { value: "single_choice", label: "Multiple Choice" },
  { value: "open_text", label: "Open Text" },
];

const CompetencyQuestionEditor: React.FC<CompetencyQuestionEditorProps> = ({ questions, onChange }) => {
  const updateQuestion = (index: number, patch: Partial<CompetencyQuestion>) => {
    const next = [...questions];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  const addQuestion = () => {
    onChange([
      ...questions,
      {
        id: `q-${Date.now()}`,
        question: "",
        type: "open_text",
        points: 50,
      },
    ]);
  };

  const removeQuestion = (index: number) => {
    onChange(questions.filter((_, i) => i !== index));
  };

  const addOption = (index: number) => {
    const question = questions[index];
    updateQuestion(index, { options: [...(question.options ?? []), ""] });
  };

  const updateOption = (qIndex: number, oIndex: number, value: string) => {
    const question = questions[qIndex];
    const options = [...(question.options ?? [])];
    options[oIndex] = value;
    updateQuestion(qIndex, { options });
  };

  const removeOption = (qIndex: number, oIndex: number) => {
    const question = questions[qIndex];
    const options = (question.options ?? []).filter((_, i) => i !== oIndex);
    const correctOptionIndex =
      question.correctOptionIndex === oIndex ? undefined : question.correctOptionIndex;
    updateQuestion(qIndex, { options, correctOptionIndex });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {questions.map((q, qIndex) => (
        <Box
          key={q.id}
          sx={{
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.12)",
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              Question {qIndex + 1}
            </Typography>
            <IconButton size="small" tooltip="Remove question" onClick={() => removeQuestion(qIndex)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          <CustomTextField
            placeholder="Question text..."
            value={q.question}
            onChange={(e) => updateQuestion(qIndex, { question: e.target.value })}
            multiline
            minRows={2}
          />

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <CustomSelect
                label="Type"
                size="small"
                value={q.type}
                onChange={(e) =>
                  updateQuestion(qIndex, {
                    type: e.target.value as CompetencyQuestion["type"],
                    ...(e.target.value !== "single_choice" ? { options: undefined, correctOptionIndex: undefined } : {}),
                  })
                }
              >
                {QUESTION_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </CustomSelect>
            </Box>
            <Box sx={{ width: 120 }}>
              <CustomTextField
                label="Points"
                size="small"
                type="number"
                value={q.points}
                onChange={(e) => updateQuestion(qIndex, { points: Number(e.target.value) || 0 })}
              />
            </Box>
          </Box>

          {q.type === "single_choice" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Options (select the correct answer)
              </Typography>
              {(q.options ?? []).map((option, oIndex) => (
                <Box key={oIndex} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Radio
                    size="small"
                    checked={q.correctOptionIndex === oIndex}
                    onChange={() => updateQuestion(qIndex, { correctOptionIndex: oIndex })}
                    sx={{ "&.Mui-checked": { color: "#614438" } }}
                  />
                  <CustomTextField
                    size="small"
                    placeholder={`Option ${oIndex + 1}`}
                    value={option}
                    onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                  />
                  <IconButton size="small" tooltip="Remove option" onClick={() => removeOption(qIndex, oIndex)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                onClick={() => addOption(qIndex)}
                startIcon={<AddIcon fontSize="small" />}
                sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700 }}
              >
                Add Option
              </Button>
            </Box>
          )}
        </Box>
      ))}

      <Button
        variant="outlined"
        onClick={addQuestion}
        startIcon={<AddIcon />}
        sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700 }}
      >
        Add Question
      </Button>
    </Box>
  );
};

export default CompetencyQuestionEditor;
